// ═══════════════════════════════════════════════════════════════════════════════
// Employer — Pending task review list (grouped by due date, same row layout as overdue)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { EmployerTaskSubPageHeader } from '../../components/task/employer/EmployerTaskSubPageHeader';
import { EmployerHistoryStatusIcon } from '../../components/task/employer/EmployerHistoryStatusIcon';
import { EmployerOverdueStatusIcon } from '../../components/task/employer/EmployerOverdueStatusIcon';
import { EmployerStatusRibbon } from '../../components/task/employer/EmployerStatusRibbon';
import { ListNavRow } from '../../components/ui/ListNavRow';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { taskInstanceApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { buildEmployerGroupedTaskDateHeading } from '../../utils/employerTaskDateHeading';
import {
  addCalendarDaysToYmd,
  getDateInTimezone,
  groupEmployerTasksByDueDateNewestFirst,
  isEmployerTaskInstanceOverdue,
} from '../../utils/employerTaskDayGrouping';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { extractEmployerTaskCompletionDisplay } from '../../utils/employerTaskCompletionDisplay';
import { APPROVAL_STATUS, type EmployerPendingReviewItem } from '@fdh/shared';

export const TaskPendingReviewPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const todayYmd = useMemo(() => getDateInTimezone(timezone), [timezone]);
  const tomorrowYmd = useMemo(() => addCalendarDaysToYmd(todayYmd, 1), [todayYmd]);
  const currentMinutes = useMemo(() => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
    const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
    return hour * 60 + minute;
  }, [timezone]);

  const [items, setItems] = useState<EmployerPendingReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await taskInstanceApi.pendingReview();
      if (response.success && response.data) {
        setItems(response.data.tasks ?? []);
      } else {
        setError(response.error ?? t('task.pendingReview.loadError'));
      }
    } catch {
      setError(t('task.pendingReview.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchPending();
  }, [fetchPending]);

  const pendingTasksGrouped = useMemo(
    () => groupEmployerTasksByDueDateNewestFirst(items),
    [items],
  );

  const handleBack = () => {
    navigateBack(navigate, '/tasks');
  };

  const handleOpenReview = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}/review`);
  };

  return (
    <div className="min-h-full bg-[#f7f8fb] dark:bg-gray-950">
      <EmployerTaskSubPageHeader title={t('task.pendingReview.title')} onBack={handleBack} />

      <main className={`mx-auto max-w-lg px-4 py-6 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center dark:bg-red-950/40">
            <p className="text-red-600 dark:text-red-300" role="alert">
              {error}
            </p>
            <button
              type="button"
              onClick={() => void fetchPending()}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none dark:text-red-200"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
            <p className="text-gray-600 dark:text-gray-300">{t('task.pendingReview.empty')}</p>
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 text-sm font-medium text-primary-600 underline hover:no-underline focus:outline-none dark:text-primary-400"
            >
              {t('task.pendingReview.backToTasks')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {pendingTasksGrouped.map((group) => (
              <section
                key={group.date}
                className="space-y-2"
                aria-labelledby={`employer-task-pending-review-date-${group.date}`}
              >
                <h2
                  id={`employer-task-pending-review-date-${group.date}`}
                  className="flex items-baseline justify-between gap-2 text-sm font-semibold tracking-tight text-gray-600 dark:text-gray-300"
                >
                  <span className="min-w-0">
                    {buildEmployerGroupedTaskDateHeading({
                      dateYmd: group.date,
                      todayYmd,
                      tomorrowYmd,
                      timezone,
                      language,
                      t,
                    })}
                  </span>
                  <span className="shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">
                    {t('task.history.groupTotalCount', { count: group.tasks.length })}
                  </span>
                </h2>
                <ul className="space-y-3" role="list">
                  {group.tasks.map((task) => {
                    const completionDisplay = extractEmployerTaskCompletionDisplay(
                      task.submittedAt,
                      task.dueDate,
                    );
                    const isOverdue = isEmployerTaskInstanceOverdue(
                      {
                        dueDate: task.dueDate,
                        dueTime: task.dueTime,
                        status: APPROVAL_STATUS.PENDING,
                        submittedAt: task.submittedAt,
                      },
                      todayYmd,
                      currentMinutes,
                    );
                    return (
                    <li key={task.taskInstanceId}>
                      <ListNavRow
                        onClick={() => handleOpenReview(task.taskInstanceId)}
                        ariaLabel={t('task.pendingReview.reviewCta', { title: task.title })}
                        tone={isOverdue ? 'danger' : 'default'}
                        comfortable
                        ribbon={<EmployerStatusRibbon status={APPROVAL_STATUS.PENDING} submittedAt={task.submittedAt} />}
                        leading={
                          isOverdue ? (
                            <EmployerOverdueStatusIcon variant="list" />
                          ) : (
                            <EmployerHistoryStatusIcon
                              status={APPROVAL_STATUS.PENDING}
                              isAwaitingSubmission={false}
                              variant="list"
                            />
                          )
                        }
                      >
                        <div className="min-w-0 pr-10">
                          <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">{task.title}</h3>
                          {completionDisplay ? (
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {t('task.pendingReview.submittedAtTime', { time: completionDisplay })}
                            </p>
                          ) : null}
                        </div>
                      </ListNavRow>
                    </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
